import { ClickAwayListener, Popper } from "@mui/material";
import { memo, useEffect, useState } from "react";
import type { LockedPositionStore, StakingPositionStore, UnwindingPositionStore } from "@/hooks";
import { PositionMenuButton } from "./PositionMenuButton";
import { PositionStatus } from "../row/position-table-row.utils";

type Position = LockedPositionStore | StakingPositionStore | UnwindingPositionStore;

interface ChangeDurationCapability {
    canChange: boolean;
    reason?: string;
}

type CanChangeDuration = (position: Position) => ChangeDurationCapability;

interface SharedPositionMenuProps {
    menuPosition: Position;
    onWithdraw: (position: Position) => void;
    onClaim: (position: Position) => void;
    onCancelUnlock: (position: Position) => void;
    canCancelUnlock: boolean;
    canStartUnlock: (position: Position) => boolean;
    canExitEarly: (position: Position) => boolean;
    onExitEarly: (position: Position) => void;
    onChangeDuration: (position: Position) => void;
    canChangeDuration: CanChangeDuration;
}

interface PositionMenuProps extends SharedPositionMenuProps {
    positionType: "locked" | "staked";
    positionStatus?: PositionStatus;
    isMenuOpen: boolean;
    menuAnchorEl: HTMLElement | null;
    onMenuClose: () => void;
}

type MenuOptionProps = SharedPositionMenuProps;

const LockedMenuOptionList = memo(
    ({
        menuPosition,
        canChangeDuration,
        canExitEarly,
        canStartUnlock,
        onChangeDuration,
        onExitEarly,
        onWithdraw,
    }: MenuOptionProps) => {
        if (!menuPosition) return null;

        return (
            <>
                <PositionMenuButton
                    onClick={() => onChangeDuration(menuPosition)}
                    disabled={!canChangeDuration(menuPosition)?.canChange}
                    tooltip={canChangeDuration(menuPosition)?.reason}
                >
                    Change duration
                </PositionMenuButton>

                {canExitEarly(menuPosition) && (
                    <PositionMenuButton onClick={() => onExitEarly(menuPosition)}>Exit early</PositionMenuButton>
                )}
                {canStartUnlock(menuPosition) && (
                    <PositionMenuButton onClick={() => onWithdraw(menuPosition)}>Begin Unlock</PositionMenuButton>
                )}
            </>
        );
    },
);

const UnlockingMenuOptionList = ({
    menuPosition,
    onCancelUnlock,
    canCancelUnlock,
    canExitEarly,
    onExitEarly,
}: MenuOptionProps) => {
    if (!menuPosition) return null;

    return (
        <>
            <PositionMenuButton onClick={() => onCancelUnlock(menuPosition)} disabled={!canCancelUnlock}>
                Cancel Unlock
            </PositionMenuButton>
            {canExitEarly(menuPosition) && (
                <PositionMenuButton onClick={() => onExitEarly(menuPosition)}>Exit early</PositionMenuButton>
            )}
        </>
    );
};

const UnlockedMenuOptionList = ({ menuPosition, onWithdraw }: MenuOptionProps) => (
    <>
        <PositionMenuButton onClick={() => onWithdraw(menuPosition)}>Withdraw</PositionMenuButton>
    </>
);

const StakedMenuOptionList = ({ menuPosition, onWithdraw }: MenuOptionProps) => {
    return (
        <>
            <PositionMenuButton onClick={() => onWithdraw(menuPosition)}>Withdraw</PositionMenuButton>
        </>
    );
};

export const PositionMenu = memo(
    ({
        positionType,
        positionStatus,
        isMenuOpen,
        menuAnchorEl,
        menuPosition,
        onMenuClose,
        onWithdraw,
        onClaim,
        onCancelUnlock,
        canCancelUnlock,
        canStartUnlock,
        canExitEarly,
        onExitEarly,
        canChangeDuration,
        onChangeDuration,
    }: PositionMenuProps) => {
        const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

        useEffect(() => {
            setPortalContainer(document.getElementById("app-theme-root"));
        }, []);

        const menuOptionProps: MenuOptionProps = {
            menuPosition,
            onChangeDuration,
            onWithdraw,
            onClaim,
            onCancelUnlock,
            canCancelUnlock,
            canStartUnlock,
            canExitEarly,
            onExitEarly,
            canChangeDuration,
        };
        const menuOptions =
            positionType === "staked" ? (
                <StakedMenuOptionList {...menuOptionProps} />
            ) : positionStatus === PositionStatus.UNLOCKED ? (
                <UnlockedMenuOptionList {...menuOptionProps} />
            ) : positionStatus === PositionStatus.UNLOCKING ? (
                <UnlockingMenuOptionList {...menuOptionProps} />
            ) : (
                <LockedMenuOptionList {...menuOptionProps} />
            );

        return (
            <Popper
                open={isMenuOpen && !!menuPosition}
                anchorEl={menuAnchorEl}
                placement="bottom-end"
                className="z-50"
                container={portalContainer}
            >
                <ClickAwayListener onClickAway={onMenuClose}>
                    <div className="card-bg mt-2 flex w-48 flex-col rounded border border-primary/20 py-1 shadow-lg">
                        {menuOptions}
                    </div>
                </ClickAwayListener>
            </Popper>
        );
    },
);

PositionMenu.displayName = "PositionMenu";
